import { Box, Button, Card, Chip, CircularProgress, Divider, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../store/store';
import ConfirmDialog from '../../common/ConfirmDialog';
import {
    cancelSubscriptionAtPeriodEnd,
    continueSubscriptionRenewal,
    selectCanCancelPaidSubscription,
    selectCanContinuePaidSubscription,
    selectHasBillingSubscriptionState,
    selectIsCancellationScheduled,
    selectEffectiveLocalSubscriptionId,
} from '../../../store/subscription-slice/subscription-slice';
import { enqueueSnackbar } from '../../../store/app-slice/app';
import { getApiResponseMessage } from '../../../services/subscriptionPaymentService';
import { formatSubscriptionDate } from '../../../utils/subscriptionFormatting';
import InfoTwoToneIcon from '@mui/icons-material/InfoTwoTone';

interface CurrentSubscriptionSectionProps {
    loading: boolean;
    onSubscriptionMutationCompleted: () => void;
}

export function CurrentSubscriptionSection({ loading, onSubscriptionMutationCompleted }: CurrentSubscriptionSectionProps) {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { billingState, subscriptionSyncPending, subscriptionActionInProgress } = useAppSelector((state) => state.subscription);
    const userInfo = useAppSelector((state) => state.profile.userInfo);
    const hasBillingSubscriptionState = useAppSelector(selectHasBillingSubscriptionState);
    const canCancelPaidSubscription = useAppSelector(selectCanCancelPaidSubscription);
    const canContinuePaidSubscription = useAppSelector(selectCanContinuePaidSubscription);
    const isCancellationScheduled = useAppSelector(selectIsCancellationScheduled);
    const effectiveLocalSubscriptionId = useAppSelector(selectEffectiveLocalSubscriptionId);

    const currentSubscription = userInfo?.currentSubscription ?? null;
    const modelsAllowed = currentSubscription?.modelsAllowed ?? null;
    const modelsSpent = currentSubscription?.modelsSpent ?? 0;
    const trainingsLeft = modelsAllowed != null ? Math.max(0, modelsAllowed - modelsSpent) : null;
    const cancellationDateLabel = formatSubscriptionDate(billingState?.cancelsAtUtc);
    const trialEndsDateLabel = formatSubscriptionDate(billingState?.trialEndsAtUtc);
    const billingStartDateLabel = formatSubscriptionDate(billingState?.startDateUtc);
    const billingEndDateLabel = formatSubscriptionDate(billingState?.endDateUtc);
    const activeLocalSubscriptionName = currentSubscription?.isActiveSubscription === true ? currentSubscription.subscriptionName : null;
    const effectiveSubscriptionName = billingState?.subscriptionName || activeLocalSubscriptionName || null;
    const effectiveSubscriptionStartDateLabel = billingStartDateLabel || formatSubscriptionDate(currentSubscription?.startDate);
    const effectiveSubscriptionEndDateLabel = billingEndDateLabel || formatSubscriptionDate(currentSubscription?.endDate);
    const currentSubscriptionStripeProductId = billingState?.subscriptionStripeProductId || billingState?.stripeProductId || null;
    const nextBillingDateLabel = formatSubscriptionDate(billingState?.nextBilling?.billingDateUtc);
    const nextBillingSubscriptionName = billingState?.nextBilling?.subscriptionName || null;
    const pendingTierPriceChange = billingState?.pendingTierPriceChange || null;
    const pendingTierPriceChangeAppliesOnDateLabel = formatSubscriptionDate(pendingTierPriceChange?.appliesOnUtc);
    const pendingTierPriceChangeAppliesInDays = typeof pendingTierPriceChange?.appliesInDays === 'number' && Number.isFinite(pendingTierPriceChange.appliesInDays)
        ? Math.max(0, pendingTierPriceChange.appliesInDays)
        : 0;
    const shouldShowPendingTierPriceChange =
        !!pendingTierPriceChange &&
        !!pendingTierPriceChange.currentStripePriceId &&
        !!pendingTierPriceChange.nextStripePriceId &&
        pendingTierPriceChange.currentStripePriceId !== pendingTierPriceChange.nextStripePriceId;
    const shouldShowNextBilling =
        !!billingState?.nextBilling?.stripeProductId &&
        !!currentSubscriptionStripeProductId &&
        !!nextBillingDateLabel;
    const effectiveSubscriptionStatus = billingState?.hasActiveTrialSubscription
        ? 'BE_MEDML_SUBSCRIPTION_TRIAL_NAME'
        : billingState?.subscriptionStatus
            || currentSubscription?.subscriptionStatus
            || (billingState?.hasActivePaidSubscription
                ? 'BE_MEDML_SUBSCRIPTION_STATUS_ACTIVE'
                : 'BE_MEDML_SUBSCRIPTION_STATUS_PENDING');
    const isEffectivelyActiveSubscription =
        billingState?.hasActivePaidSubscription === true ||
        billingState?.hasActiveTrialSubscription === true ||
        currentSubscription?.isActiveSubscription === true ||
        effectiveSubscriptionStatus === 'BE_MEDML_SUBSCRIPTION_STATUS_ACTIVE';

    const [confirmDialog, setConfirmDialog] = useState<{
        open: boolean;
        titleKey: string;
        bodyKey: string;
        action: 'cancel' | 'continue' | null;
        color: 'primary' | 'error';
    }>({ open: false, titleKey: '', bodyKey: '', action: null, color: 'primary' });

    const closeConfirmDialog = () => setConfirmDialog((prev) => ({ ...prev, open: false }));

    const sendFeedback = (messageKey: string, isError = false) => {
        dispatch(enqueueSnackbar({
            message: t(messageKey),
            options: { variant: isError ? 'error' : 'success' },
        }));
    };

    const handleCancelPaidSubscription = () => {
        if (!effectiveLocalSubscriptionId || subscriptionActionInProgress) return;
        setConfirmDialog({ open: true, titleKey: 'PROFILE_CONFIRM_CANCEL_TITLE', bodyKey: 'PROFILE_CONFIRM_CANCEL_BODY', action: 'cancel', color: 'error' });
    };

    const handleContinuePaidSubscription = () => {
        if (!effectiveLocalSubscriptionId || subscriptionActionInProgress) return;
        setConfirmDialog({ open: true, titleKey: 'PROFILE_CONFIRM_CONTINUE_TITLE', bodyKey: 'PROFILE_CONFIRM_CONTINUE_BODY', action: 'continue', color: 'primary' });
    };

    const handleConfirmDialogSubmit = async () => {
        closeConfirmDialog();
        if (confirmDialog.action === 'cancel') {
            const result = await dispatch(cancelSubscriptionAtPeriodEnd({ localSubscriptionId: effectiveLocalSubscriptionId! }));
            if (cancelSubscriptionAtPeriodEnd.rejected.match(result)) {
                sendFeedback(result.payload || getApiResponseMessage(null), true);
                return;
            }
            onSubscriptionMutationCompleted();
            sendFeedback('PROFILE_CANCEL_SUCCESS');
        } else if (confirmDialog.action === 'continue') {
            const result = await dispatch(continueSubscriptionRenewal({ localSubscriptionId: effectiveLocalSubscriptionId! }));
            if (continueSubscriptionRenewal.rejected.match(result)) {
                sendFeedback(result.payload || getApiResponseMessage(null), true);
                return;
            }
            onSubscriptionMutationCompleted();
            sendFeedback('PROFILE_CONTINUE_SUCCESS');
        }
    };

    if (loading) {
        return (
            <Card
                variant="outlined"
                sx={{
                    borderRadius: 3.5,
                    borderColor: 'var(--selected-table-row-color)',
                    minHeight: 320,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'background.paper',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
                }}
            >
                <Stack spacing={1.8} alignItems="center" sx={{ p: 4 }}>
                    <CircularProgress size={40} sx={{ color: 'var(--button-background-color)' }} />
                    <Typography sx={{ color: 'var(--link-text-color)', fontWeight: 600 }}>
                        {t('PROFILE_LOADING_SUBSCRIPTION_DETAILS')}
                    </Typography>
                </Stack>
            </Card>
        );
    }

    return (
        <>
        <Card
            variant="outlined"
            sx={{
                mb: 2,
                borderRadius: 3.5,
                borderColor: 'var(--selected-table-row-color)',
                backgroundColor: 'background.paper',
                overflow: 'hidden',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
            }}
        >
            <Box
                sx={{
                    px: { xs: 1.5, md: 2.5 },
                    py: { xs: 1, md: 1.5 },
                    borderBottom: '1px solid var(--selected-table-row-color)',
                    backgroundColor: 'action.hover',
                }}
            >
                <Stack direction="row" spacing={1.5} justifyContent="space-between" alignItems="center">
                    <Typography variant="h6" sx={{ fontWeight: 700, fontSize: { xs: '1.1rem', md: '1.25rem' } }}>
                        {t('PROFILE_CURRENT_SUBSCRIPTION_PREVIEW')}
                    </Typography>
                    {hasBillingSubscriptionState && (
                        <Chip
                            size="small"
                            label={isEffectivelyActiveSubscription ? t('PROFILE_ACTIVE_SUBSCRIPTION') : t('PROFILE_INACTIVE_SUBSCRIPTION')}
                            sx={{
                                backgroundColor: 'var(--button-background-color)',
                                color: 'var(--button-text-color)',
                                fontWeight: 600,
                                height: 28,
                                fontSize: '0.8rem',
                            }}
                        />
                    )}
                </Stack>
            </Box>

            <Box sx={{ p: { xs: 1.5, md: 2.5 } }}>
                {hasBillingSubscriptionState ? (
                    <Stack spacing={1.5}>
                        {/* Subscription Name and Status */}
                        <Box
                            sx={{
                                p: { xs: 1, sm: 1.75 },
                                borderRadius: 3,
                                border: '1.5px solid var(--selected-table-row-color)',
                                backgroundColor: 'background.paper',
                                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
                            }}
                        >
                            <Stack spacing={1.5}>
                                <Box>
                                    <Typography variant="caption" sx={{ color: 'var(--link-text-color)', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 0.5 }}>
                                        {t('USERMANAGEMENT_ACTIVE_SUBSCRIPTION_NAME_COLUMN')}
                                    </Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 700, mt: 0.5, fontSize: '1.15rem' }}>
                                        {effectiveSubscriptionName ? t(effectiveSubscriptionName) : '-'}
                                    </Typography>
                                </Box>
                                
                                <Divider sx={{ borderColor: 'var(--selected-table-row-color)', opacity: 0.5 }} />
                                
                                <Box>
                                    <Typography variant="caption" sx={{ color: 'var(--link-text-color)', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 0.5 }}>
                                        {t('USERMANAGEMENT_IS_ACTIVE_SUBSCRIPTION_COLUMN')}
                                    </Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 600, mt: 0.5 }}>
                                        {effectiveSubscriptionStatus === 'BE_MEDML_SUBSCRIPTION_STATUS_ACTIVE' && isCancellationScheduled && cancellationDateLabel
                                            ? t('PROFILE_SUBSCRIPTION_STATUS_CANCELS_ON', { date: cancellationDateLabel })
                                            : t(effectiveSubscriptionStatus)}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Box>

                        {currentSubscription != null && isEffectivelyActiveSubscription && (
                            <Box
                                sx={{
                                    p: { xs: 1, sm: 1.5 },
                                    borderRadius: 3,
                                    border: trainingsLeft === 0 ? '1.5px solid' : '1.5px solid var(--selected-table-row-color)',
                                    borderColor: trainingsLeft === 0 ? 'error.main' : 'var(--selected-table-row-color)',
                                    backgroundColor: trainingsLeft === 0 ? 'rgba(255, 0, 0, 0.03)' : 'background.paper',
                                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
                                }}
                            >
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'var(--link-text-color)' }}>
                                        {t('SUBSCRIPTION_TRAININGS_REMAINING_LABEL')}
                                    </Typography>
                                    <Typography variant="h5" sx={{ fontWeight: 700, color: trainingsLeft === 0 ? 'error.main' : 'inherit', fontSize: '1.25rem' }}>
                                        {trainingsLeft != null ? trainingsLeft : t('SUBSCRIPTION_TRAININGS_REMAINING_UNLIMITED')}
                                    </Typography>
                                </Stack>
                            </Box>
                        )}

                        {subscriptionSyncPending && (
                            <Stack direction="row" spacing={1.2} alignItems="center" sx={{ px: 0.5 }}>
                                <CircularProgress size={16} />
                                <Typography variant="body2" sx={{ color: 'var(--link-text-color)', fontWeight: 500 }}>
                                    {t('PROFILE_LOADING_SUBSCRIPTION_DETAILS')}
                                </Typography>
                            </Stack>
                        )}

                        {/* Subscription Dates and Notices */}
                        <Box
                            sx={{
                                p: { xs: 1, sm: 1.5 },
                                borderRadius: 3,
                                border: '1px solid var(--selected-table-row-color)',
                                backgroundColor: 'action.hover',
                                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)',
                            }}
                        >
                            <Stack spacing={1.5}>
                                {/* Dates Grid */}
                                <Stack spacing={1}>
                                    {effectiveSubscriptionStartDateLabel && (
                                        <Box>
                                            <Typography variant="caption" sx={{ color: 'var(--link-text-color)', fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                                {t('USERMANAGEMENT_ACTIVE_SUBSCRIPTION_START_DATE_COLUMN')}
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.3 }}>
                                                {effectiveSubscriptionStartDateLabel}
                                            </Typography>
                                        </Box>
                                    )}
                                    {effectiveSubscriptionEndDateLabel && (
                                        <Box>
                                            <Typography variant="caption" sx={{ color: 'var(--link-text-color)', fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                                {t('USERMANAGEMENT_ACTIVE_SUBSCRIPTION_END_DATE_COLUMN')}
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.3 }}>
                                                {effectiveSubscriptionEndDateLabel}
                                            </Typography>
                                        </Box>
                                    )}
                                </Stack>

                                {/* Trial and Cancellation Notices */}
                                {((billingState?.hasActiveTrialSubscription && trialEndsDateLabel) || (isCancellationScheduled && cancellationDateLabel)) && (
                                    <>
                                        <Divider sx={{ borderColor: 'var(--selected-table-row-color)', opacity: 0.6 }} />
                                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} useFlexGap flexWrap="wrap">
                                            {billingState?.hasActiveTrialSubscription && trialEndsDateLabel && (
                                                <Chip
                                                    size="small"
                                                    label={`${t('PROFILE_TRIAL_ENDS_ON_LABEL')}: ${trialEndsDateLabel}`}
                                                    sx={{
                                                        backgroundColor: 'var(--button-background-color)',
                                                        color: 'var(--button-text-color)',
                                                        fontWeight: 600,
                                                        height: 30,
                                                        fontSize: '0.8rem',
                                                    }}
                                                />
                                            )}
                                            {isCancellationScheduled && cancellationDateLabel && (
                                                <Chip
                                                    size="small"
                                                    variant="outlined"
                                                    label={t('PROFILE_SUBSCRIPTION_STATUS_CANCELS_ON', { date: cancellationDateLabel })}
                                                    sx={{
                                                        borderColor: 'var(--selected-table-row-color)',
                                                        borderWidth: 1.5,
                                                        color: 'var(--link-text-color)',
                                                        fontWeight: 600,
                                                        height: 30,
                                                        fontSize: '0.8rem',
                                                    }}
                                                />
                                            )}
                                        </Stack>
                                    </>
                                )}

                                {/* Next Billing Info */}
                                {shouldShowNextBilling && (
                                    <>
                                        <Divider sx={{ borderColor: 'var(--selected-table-row-color)', opacity: 0.6 }} />
                                        <Box sx={{ display: 'flex', flexDirection: 'row', gap: .5, alignItems: 'center' }}>
                                            <InfoTwoToneIcon className="info-icon" />
                                            <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 500 }}>
                                                {t('PROFILE_NEXT_BILLING_MESSAGE', {
                                                    date: nextBillingDateLabel,
                                                    planName: nextBillingSubscriptionName ? t(nextBillingSubscriptionName) : (effectiveSubscriptionName ? t(effectiveSubscriptionName) : '-'),
                                                })}
                                            </Typography>
                                        </Box>
                                    </>
                                )}
                            </Stack>
                        </Box>

                        {/* Pending Price Change Alert */}
                        {shouldShowPendingTierPriceChange && (
                            <Box
                                sx={{
                                    p: { xs: 2, sm: 2.5 },
                                    borderRadius: 3,
                                    border: '1.5px solid var(--selected-table-row-color)',
                                    backgroundColor: 'action.hover',
                                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
                                }}
                            >
                                <Stack spacing={1.5}>
                                    <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.9rem' }}>
                                        {t('PROFILE_PENDING_PRICE_CHANGE_TITLE')}
                                    </Typography>
                                    
                                    <Stack direction="row" spacing={3} sx={{ flexWrap: 'wrap', gap: 1.5 }}>
                                        <Box>
                                            <Typography variant="caption" sx={{ color: 'var(--link-text-color)', fontWeight: 600, fontSize: '0.7rem' }}>
                                                {t('PROFILE_PENDING_PRICE_CHANGE_CURRENT_PRICE')}
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.3 }}>
                                                {pendingTierPriceChange?.currentPriceDisplay || '-'}
                                            </Typography>
                                        </Box>
                                        <Box>
                                            <Typography variant="caption" sx={{ color: 'var(--link-text-color)', fontWeight: 600, fontSize: '0.7rem' }}>
                                                {t('PROFILE_PENDING_PRICE_CHANGE_NEXT_PRICE')}
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.3 }}>
                                                {pendingTierPriceChange?.nextPriceDisplay || '-'}
                                            </Typography>
                                        </Box>
                                    </Stack>
                                    
                                    <Typography variant="body2" sx={{ color: 'var(--link-text-color)', fontSize: '0.85rem' }}>
                                        {t('PROFILE_PENDING_PRICE_CHANGE_APPLIES', {
                                            days: pendingTierPriceChangeAppliesInDays,
                                            date: pendingTierPriceChangeAppliesOnDateLabel || '-',
                                        })}
                                    </Typography>
                                </Stack>
                            </Box>
                        )}

                        {/* Action Buttons */}
                        {(canCancelPaidSubscription || canContinuePaidSubscription) && (
                            <>
                                <Divider sx={{ borderColor: 'var(--selected-table-row-color)', mt: 1 }} />
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ pt: 0.5 }}>
                                    {canCancelPaidSubscription && (
                                        <Button
                                            fullWidth
                                            variant="outlined"
                                            color="error"
                                            disabled={subscriptionActionInProgress !== null}
                                            onClick={handleCancelPaidSubscription}
                                            sx={{
                                                maxWidth: { sm: 240 },
                                                fontWeight: 600,
                                                borderWidth: 1.5,
                                                minHeight: 44,
                                                borderRadius: 2,
                                                textTransform: 'none',
                                                fontSize: '0.95rem',
                                                '&:hover': {
                                                    borderWidth: 1.5,
                                                },
                                            }}
                                        >
                                            {subscriptionActionInProgress === 'cancel'
                                                ? <CircularProgress size={20} color="inherit" />
                                                : t('PROFILE_CANCEL_BUTTON')}
                                        </Button>
                                    )}
                                    {canContinuePaidSubscription && (
                                        <Button
                                            fullWidth
                                            variant="contained"
                                            disabled={subscriptionActionInProgress !== null}
                                            onClick={handleContinuePaidSubscription}
                                            className="button-color"
                                            sx={{
                                                maxWidth: { sm: 240 },
                                                fontWeight: 700,
                                                minHeight: 44,
                                                borderRadius: 2,
                                                textTransform: 'none',
                                                fontSize: '0.95rem',
                                                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                                                '&:hover': {
                                                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)',
                                                },
                                            }}
                                        >
                                            {subscriptionActionInProgress === 'continue'
                                                ? <CircularProgress size={20} color="inherit" />
                                                : t('PROFILE_CONTINUE_BUTTON')}
                                        </Button>
                                    )}
                                </Stack>
                            </>
                        )}
                    </Stack>
                ) : (
                    <Typography sx={{ color: 'var(--link-text-color)', textAlign: 'center', py: 4, fontWeight: 500 }}>
                        {t('PROFILE_NO_CURRENT_SUBSCRIPTION')}
                    </Typography>
                )}
            </Box>
        </Card>
        <ConfirmDialog
            open={confirmDialog.open}
            title={t(confirmDialog.titleKey)}
            bodyText={t(confirmDialog.bodyKey)}
            isLoading={false}
            onClose={closeConfirmDialog}
            onConfirm={handleConfirmDialogSubmit}
            cancelButtonText={t('CANCEL_BUTTON')}
            confirmButtonText={t('SUBMIT_BUTTON')}
            confirmColor={confirmDialog.color}
        />
        </>
    );
}